import moment from "moment";

// =============================================
// Расчёт позиций
// =============================================

/**
 * Конвертация даты в пиксельную позицию относительно viewStart
 */
export function dateToPixel(date, viewStart, pixelsPerMs) {
  const ms = moment(date).valueOf() - moment(viewStart).valueOf();
  return ms * pixelsPerMs;
}

/**
 * Ширина блока бронирования в пикселях
 */
export function bookingWidth(startDate, endDate, pixelsPerMs) {
  const ms = moment(endDate).valueOf() - moment(startDate).valueOf();
  return Math.max(ms * pixelsPerMs, 2); // минимум 2px
}

/**
 * Общая ширина таймлайна в пикселях
 */
export function totalTimelineWidth(viewStart, viewEnd, pixelsPerMs) {
  const ms = moment(viewEnd).valueOf() - moment(viewStart).valueOf();
  return ms * pixelsPerMs;
}

// =============================================
// Шкала времени
// =============================================

/**
 * Генерация ячеек верхнего уровня (месяцы для daily, дни для hourly)
 */
export function generateTopLevelDates(viewStart, viewEnd, mode) {
  const dates = [];
  const start = moment(viewStart);
  const end = moment(viewEnd);

  if (mode === "daily") {
    let current = start.clone().startOf("month");
    while (current.isBefore(end)) {
      const monthEnd = current.clone().endOf("month");
      const visibleStart = moment.max(current, start);
      const visibleEnd = moment.min(monthEnd, end);
      const days = visibleEnd.diff(visibleStart, "days") + 1;
      dates.push({
        date: current.clone(),
        label: current.format("MMMM YYYY"),
        days,
        isCurrentMonth: current.isSame(moment(), "month"),
      });
      current.add(1, "month").startOf("month");
    }
  } else {
    // hourly mode — дни как верхний уровень
    let current = start.clone().startOf("day");
    while (current.isBefore(end)) {
      const dayEnd = current.clone().endOf("day");
      const visibleStart = moment.max(current, start);
      const visibleEnd = moment.min(dayEnd, end);
      const hours = visibleEnd.diff(visibleStart, "hours") + 1;
      dates.push({
        date: current.clone(),
        label: current.format("dd, D MMM"),
        hours: Math.min(hours, 24),
        isToday: current.isSame(moment(), "day"),
        isWeekend: current.day() === 0 || current.day() === 6,
      });
      current.add(1, "day");
    }
  }
  return dates;
}

/**
 * Генерация ячеек нижнего уровня (дни для daily, часы для hourly)
 */
export function generateBottomLevelDates(viewStart, viewEnd, mode) {
  const dates = [];
  const start = moment(viewStart);
  const end = moment(viewEnd);

  if (mode === "daily") {
    let current = start.clone().startOf("day");
    while (current.isBefore(end)) {
      dates.push({
        date: current.clone(),
        label: current.format("D"),
        dayOfWeek: current.format("dd"),
        isToday: current.isSame(moment(), "day"),
        isWeekend: current.day() === 0 || current.day() === 6,
      });
      current.add(1, "day");
    }
  } else {
    // hourly mode
    let current = start.clone().startOf("hour");
    while (current.isBefore(end)) {
      dates.push({
        date: current.clone(),
        label: current.format("H"),
        isNow:
          current.isSame(moment(), "hour") &&
          current.isSame(moment(), "day"),
        isWeekend: current.day() === 0 || current.day() === 6,
      });
      current.add(1, "hour");
    }
  }
  return dates;
}

// =============================================
// Цвета бронирований (порт из NewCalendar.vue)
// =============================================

/**
 * Сумма оплат аренды (порт calculateRentPaymentsSum)
 */
export function calcRentPaymentsSum(counts, multicurrencyCashboxes) {
  if (!counts || counts.length === 0) return 0;
  let paid = 0;
  counts.forEach((count) => {
    if (count.group === "Оплата аренды" && count.completed) {
      const sum =
        multicurrencyCashboxes && count.rated_sum != null
          ? Number(count.rated_sum)
          : Number(count.sum);
      if (count.operation) {
        paid += sum;
      } else {
        paid -= sum;
      }
    }
  });
  return parseFloat(paid.toFixed(2));
}

/**
 * Оплаченные дни (порт paidDays)
 */
export function paidDays(booking, counts, multicurrencyCashboxes) {
  if (!booking.days || !booking.rental_cost) return 0;
  const rentSum = calcRentPaymentsSum(counts, multicurrencyCashboxes);
  let days =
    Math.round(rentSum / (booking.rental_cost / booking.days)) || 0;
  if (booking.gifts && booking.gifts.length > 0) {
    let giftPaid = 0;
    booking.gifts.forEach((gift) => {
      if (gift.gift_type === 0) {
        giftPaid += gift.count * gift.price;
      }
    });
    days =
      Math.round(
        (rentSum / ((booking.rental_cost + giftPaid) / days)) * 100
      ) / 100;
  }
  return days;
}

/**
 * Оплаченные часы (порт paidHours)
 */
export function paidHours(booking, counts, multicurrencyCashboxes) {
  if (!booking.hourly_hours || !booking.rental_cost) return 0;
  const rentSum = calcRentPaymentsSum(counts, multicurrencyCashboxes);
  let hours =
    Math.round(rentSum / (booking.rental_cost / booking.hourly_hours)) ||
    0;
  if (booking.gifts && booking.gifts.length > 0) {
    let giftPaid = 0;
    booking.gifts.forEach((gift) => {
      if (gift.gift_type === 0) {
        giftPaid += gift.count * gift.price;
      }
    });
    hours =
      Math.round(
        (rentSum / ((booking.rental_cost + giftPaid) / hours)) * 100
      ) / 100;
  }
  return hours;
}

/**
 * Оплаченные недели (порт paidWeeks)
 */
export function paidWeeks(booking, counts, multicurrencyCashboxes) {
  if (!booking.weekly_weeks || !booking.rental_cost) return 0;
  const rentSum = calcRentPaymentsSum(counts, multicurrencyCashboxes);
  let weeks =
    Math.round(
      rentSum / (booking.rental_cost / booking.weekly_weeks)
    ) || 0;
  if (booking.gifts && booking.gifts.length > 0) {
    let giftPaid = 0;
    booking.gifts.forEach((gift) => {
      if (gift.gift_type === 0) {
        giftPaid += gift.count * gift.price;
      }
    });
    weeks =
      Math.round(
        (rentSum / ((booking.rental_cost + giftPaid) / weeks)) * 100
      ) / 100;
  }
  return weeks;
}

/**
 * Оплаченные месяцы (порт paidMonths)
 */
export function paidMonths(booking, counts, multicurrencyCashboxes) {
  if (!booking.monthly_months || !booking.rental_cost) return 0;
  const rentSum = calcRentPaymentsSum(counts, multicurrencyCashboxes);
  let months =
    Math.round(
      rentSum / (booking.rental_cost / booking.monthly_months)
    ) || 0;
  if (booking.gifts && booking.gifts.length > 0) {
    let giftPaid = 0;
    booking.gifts.forEach((gift) => {
      if (gift.gift_type === 0) {
        giftPaid += gift.count * gift.price;
      }
    });
    months =
      Math.round(
        (rentSum / ((booking.rental_cost + giftPaid) / months)) * 100
      ) / 100;
  }
  return months;
}

/**
 * % оплаты (порт progressPercent)
 */
export function calcProgressPercent(booking, counts, multicurrencyCashboxes) {
  // Договор выкупа: платежи — «Аренда с выкупом», rental_cost — начисленное на
  // сегодня, days — весь срок, расчёт ниже дал бы 0. Полоска — весь срок
  // договора, её доля оплаты — доля договора из api (оплачено / график).
  if (booking.rent_to_own === true && booking.rent_to_own_paid_percent != null) {
    return Number(booking.rent_to_own_paid_percent);
  }
  const rentSum = calcRentPaymentsSum(counts, multicurrencyCashboxes);
  let divisor, units;

  if (!booking.hourly && !booking.weekly && !booking.monthly) {
    units = paidDays(booking, counts, multicurrencyCashboxes);
    divisor = (booking.rental_cost - booking.aggr_commission) / units;
  } else if (booking.hourly) {
    units = paidHours(booking, counts, multicurrencyCashboxes);
    divisor = (booking.rental_cost - booking.aggr_commission) / units;
  } else if (booking.weekly) {
    units = paidWeeks(booking, counts, multicurrencyCashboxes);
    divisor = (booking.rental_cost - booking.aggr_commission) / units;
  } else if (booking.monthly) {
    units = paidMonths(booking, counts, multicurrencyCashboxes);
    divisor = (booking.rental_cost - booking.aggr_commission) / units;
  }

  if (!units || !divisor || !isFinite(divisor)) return 0;
  // Две цифры после запятой, а не целые: процент берётся от всей полоски,
  // и у долгой аренды день оплаты — доля процента (3 из 734 = 0,41 %).
  return Math.round((rentSum / divisor / units) * 10000) / 100;
}

/**
 * Цвет фона бронирования (порт bookingColor + selectColorPaid)
 * Возвращает CSS background string
 */
export function getBookingBackground(booking, progressPct) {
  // Порядок важен: technical+in_rent проверяется ДО in_rent без technical
  if (booking.in_rent && booking.technical) {
    return "rgba(158,158,158,0.6)";
  } else if (booking.in_rent && !booking.technical) {
    let pct = progressPct;
    if (pct >= 100) pct = 100;
    if (pct <= 0) pct = 0;
    return `linear-gradient(to right, rgba(76,175,80,0.85) ${pct}%, rgba(244,67,54,0.6) ${pct}%)`;
  } else if (booking.ride) {
    return "rgba(255,152,0,0.7)";
  } else if (booking.technical) {
    return "rgba(158,158,158,0.5)";
  } else {
    return "rgba(30,136,229,0.85)";
  }
}

/**
 * Цвет текста на бронировании (для контраста)
 */
export function getBookingTextColor(booking) {
  if (booking.ride) return "#333"; // тёмный на оранжевом
  if (booking.technical && !booking.in_rent) return "#333"; // тёмный на бледно-сером
  if (booking.in_rent && booking.technical) return "#333"; // тёмный на сером
  return "#fff";
}

/**
 * Цвет рамки бронирования (для technical + in_rent)
 */
export function getBookingBorder(booking) {
  if (booking.in_rent && booking.technical) {
    return "2px solid rgba(76,175,80,0.7)";
  }
  return "none";
}

/**
 * Текст на бронировании (порт contentOnBooking)
 */
export function getBookingContent(booking, financeMode, paidUnitsInfo) {
  if (financeMode && paidUnitsInfo) {
    return paidUnitsInfo;
  }
  const startTime = moment(
    booking.start_date,
    "DD-MM-YYYY H:mm"
  ).format("H:mm");
  const endTime = moment(booking.end_date, "DD-MM-YYYY H:mm").format(
    "H:mm"
  );
  return `${booking.location_start || ""} ${startTime} - ${booking.location_end || ""} ${endTime}`;
}

/**
 * Текст для finance mode (оплачено X из Y единиц)
 */
export function getFinanceText(booking, counts, multicurrencyCashboxes, t) {
  const from = t ? t("other.from") : "/";
  const daysLabel = t ? t("custom.days_short") : "d.";
  const hoursLabel = t ? t("custom.hours_short") : "h.";
  const weeksLabel = t ? t("custom.weeks_short") : "w.";
  const monthsLabel = t ? t("custom.months_short") : "mo.";

  if (!booking.hourly && !booking.weekly && !booking.monthly) {
    const paid = paidDays(booking, counts, multicurrencyCashboxes);
    return `${paid} ${from} ${booking.days} ${daysLabel}`;
  } else if (booking.hourly) {
    const paid = paidHours(booking, counts, multicurrencyCashboxes);
    return `${paid} ${from} ${booking.hourly_hours} ${hoursLabel}`;
  } else if (booking.weekly) {
    const paid = paidWeeks(booking, counts, multicurrencyCashboxes);
    return `${paid} ${from} ${booking.weekly_weeks} ${weeksLabel}`;
  } else if (booking.monthly) {
    const paid = paidMonths(booking, counts, multicurrencyCashboxes);
    return `${paid} ${from} ${booking.monthly_months} ${monthsLabel}`;
  }
  return "";
}

/**
 * Срок брони в единицах её типа: «6 дн.», «1 нед.», «2 мес.», «5 ч.».
 * booking.days — юниты тарификации, а не сутки (у понедельной брони это
 * недели), поэтому единицу выбираем по флагу типа, как в getFinanceText.
 */
export function getDurationText(booking, t) {
  let units;
  let labelKey;
  let fallback;
  if (booking.hourly) {
    [units, labelKey, fallback] = [booking.hourly_hours, "custom.hours_short", "h."];
  } else if (booking.weekly) {
    [units, labelKey, fallback] = [booking.weekly_weeks, "custom.weeks_short", "w."];
  } else if (booking.monthly) {
    [units, labelKey, fallback] = [booking.monthly_months, "custom.months_short", "mo."];
  } else {
    [units, labelKey, fallback] = [booking.days, "custom.days_short", "d."];
  }
  if (!units) return "";
  return `${units} ${t ? t(labelKey) : fallback}`;
}

// =============================================
// Маппинг API данных
// =============================================
// Специальные режимы отображения
// =============================================

/**
 * Режим "Возвраты/Выдачи" — определяет тип бронирования относительно сегодня
 * Возвращает: 'checkout_today' | 'checkout_tomorrow' | 'checkin_today' | 'checkin_tomorrow' | null
 */
export function getHandoverType(booking) {
  if (!booking._start || !booking._end) return null;
  const now = moment();
  const today = now.clone().startOf("day");
  const tomorrow = today.clone().add(1, "day");
  const dayAfter = tomorrow.clone().add(1, "day");

  const endDay = booking._end.clone().startOf("day");
  const startDay = booking._start.clone().startOf("day");

  if (booking.in_rent || booking.active) {
    // Возврат сегодня
    if (endDay.isSame(today, "day")) return "checkout_today";
    // Возврат завтра
    if (endDay.isSame(tomorrow, "day")) return "checkout_tomorrow";
  }
  if (booking.active && !booking.in_rent) {
    // Выдача сегодня
    if (startDay.isSame(today, "day")) return "checkin_today";
    // Выдача завтра
    if (startDay.isSame(tomorrow, "day")) return "checkin_tomorrow";
  }
  return null;
}

/**
 * Стиль обводки для режима "Возвраты/Выдачи"
 */
export function getHandoverBorder(type) {
  switch (type) {
    case "checkout_today": return "3px solid #f44336"; // красный
    case "checkout_tomorrow": return "3px solid #ff9800"; // оранжевый
    case "checkin_today": return "3px solid #4caf50"; // зелёный
    case "checkin_tomorrow": return "3px solid #8bc34a"; // светло-зелёный
    default: return "none";
  }
}

/**
 * Иконка-маркер для режима "Возвраты/Выдачи"
 */
export function getHandoverMarker(type) {
  switch (type) {
    case "checkout_today": return "↙";
    case "checkout_tomorrow": return "↙";
    case "checkin_today": return "↗";
    case "checkin_tomorrow": return "↗";
    default: return "";
  }
}

/**
 * Просроченное начало — бронь активна, не в аренде, не завершена,
 * не техническая, и start_date просрочен на 12+ часов.
 * Возвращает { overdue: bool, hours: number }
 */
export function isBookingOverdue(booking) {
  const THRESHOLD_HOURS = 12;
  if (!booking._start || booking.in_rent || booking.ride || booking.technical || booking.active === false) {
    return { overdue: false, hours: 0 };
  }
  const now = moment();
  const hoursPassed = now.diff(booking._start, "hours", true);
  if (hoursPassed >= THRESHOLD_HOURS) {
    return { overdue: true, hours: Math.floor(hoursPassed) };
  }
  return { overdue: false, hours: 0 };
}

/**
 * Режим "Просрочки" — проверяет просроченность возврата бронирования
 * Возвращает кол-во просроченных дней или 0
 */
export function getOverdueDays(booking) {
  if (!booking._end || !booking.in_rent) return 0;
  const now = moment();
  if (booking._end.isBefore(now)) {
    return Math.ceil(now.diff(booking._end, "hours") / 24);
  }
  return 0;
}

/**
 * Режим "Окна простоя" — находит промежутки между бронированиями для авто
 * Возвращает массив { start: moment, end: moment, days: number }
 */
export function findIdleGaps(bookings, viewStart, viewEnd) {
  if (!bookings || bookings.length === 0) {
    return [{ start: moment(viewStart), end: moment(viewEnd), days: moment(viewEnd).diff(moment(viewStart), "days") }];
  }

  // Сортировка по дате начала
  const sorted = [...bookings]
    .filter((b) => b._start && b._end)
    .sort((a, b) => a._start.valueOf() - b._start.valueOf());

  const gaps = [];
  let cursor = moment(viewStart);

  sorted.forEach((b) => {
    if (b._start.isAfter(cursor)) {
      const gapDays = b._start.diff(cursor, "days");
      if (gapDays >= 1) {
        gaps.push({ start: cursor.clone(), end: b._start.clone(), days: gapDays });
      }
    }
    if (b._end.isAfter(cursor)) {
      cursor = b._end.clone();
    }
  });

  // Промежуток после последнего бронирования
  if (cursor.isBefore(moment(viewEnd))) {
    const gapDays = moment(viewEnd).diff(cursor, "days");
    if (gapDays >= 1) {
      gaps.push({ start: cursor.clone(), end: moment(viewEnd), days: gapDays });
    }
  }

  return gaps;
}

/**
 * Режим "Тепловая карта" — определяет занятость ячейки дня для авто
 * Возвращает: 'free' | 'partial' | 'busy'
 */
export function getDayCellStatus(dayStart, dayEnd, bookings) {
  if (!bookings || bookings.length === 0) return "free";
  const dStart = moment(dayStart).valueOf();
  const dEnd = moment(dayEnd).valueOf();

  let coveredMs = 0;
  const totalMs = dEnd - dStart;

  bookings.forEach((b) => {
    if (!b._start || !b._end) return;
    const bStart = Math.max(b._start.valueOf(), dStart);
    const bEnd = Math.min(b._end.valueOf(), dEnd);
    if (bEnd > bStart) coveredMs += bEnd - bStart;
  });

  const ratio = coveredMs / totalMs;
  if (ratio >= 0.9) return "busy";
  if (ratio > 0.1) return "partial";
  return "free";
}

// =============================================
// Стекинг (наложение бронирований)
// =============================================

/**
 * Распределяет бронирования по дорожкам (lanes) чтобы
 * наложенные не перекрывали друг друга.
 * Возвращает Map<bookingId, laneIndex> и maxLane
 */
export function assignLanes(bookings) {
  if (!bookings || bookings.length === 0) return { lanes: {}, maxLane: 0 };

  // Сортируем по дате начала, при равных — по длительности (длинные первыми)
  const sorted = [...bookings].sort((a, b) => {
    const diff = a._start.valueOf() - b._start.valueOf();
    if (diff !== 0) return diff;
    return (b._end.valueOf() - b._start.valueOf()) - (a._end.valueOf() - a._start.valueOf());
  });

  const lanes = {};
  const laneEnds = []; // laneEnds[i] = timestamp окончания последнего элемента в lane i

  sorted.forEach((booking) => {
    const start = booking._start.valueOf();
    let placed = false;
    for (let i = 0; i < laneEnds.length; i++) {
      if (laneEnds[i] <= start) {
        lanes[booking.id] = i;
        laneEnds[i] = booking._end.valueOf();
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes[booking.id] = laneEnds.length;
      laneEnds.push(booking._end.valueOf());
    }
  });

  return { lanes, maxLane: laneEnds.length };
}

// Геометрия строки (держать в синхроне с GanttTimelineItem: top = 3 + lane*(h+2))
const LANE_UNIT = 24; // высота дорожки
const LANE_GAP = 2; // ITEM_GAP
const LANE_PADDING = 3; // ITEM_PADDING

/**
 * Детерминированная высота строки авто.
 *
 * ВАЖНО: единый источник высоты и для левой панели (ярлыки), и для правой
 * (таймлайн), и для виртуального скролла (rowPositions/offsetY). Раньше высота
 * измерялась лениво — строка рендерилась, считала свою высоту и отдавала её
 * наверх (row-height-changed). При >100 авто и быстрой прокрутке многие
 * многодорожечные строки прокручивались мимо, не успев измериться, их высота
 * оставалась заниженной (baseRowHeight), и ошибка накапливалась к низу списка —
 * ярлыки уезжали относительно брони. Чистая функция от броней авто убирает
 * ленивость и рассинхрон.
 */
export function computeRowHeight(bookings, baseRowHeight, compactMode) {
  if (compactMode) return baseRowHeight;
  const maxLanes = Math.max(1, assignLanes(bookings).maxLane);
  if (maxLanes <= 1) return baseRowHeight;
  return Math.max(
    baseRowHeight,
    maxLanes * LANE_UNIT + (maxLanes - 1) * LANE_GAP + LANE_PADDING * 2
  );
}

// =============================================
// Маппинг API данных
// =============================================

/**
 * Парсинг ответа API index_for_calendar
 * Возвращает { cars: [...], bookings: [...] }
 */
export function mapApiResponse(responseData) {
  const cars = responseData.cars.data.map((car) => car.attributes);

  const bookings = responseData.bookings.data.map((element) => {
    const booking = { ...element.attributes };
    booking.counts = [];
    booking.gifts = [];

    if (element.relationships.counts && element.relationships.counts.data) {
      element.relationships.counts.data.forEach((item) => {
        if (responseData.bookings.included) {
          responseData.bookings.included
            .filter((inc) => inc.id == item.id && inc.type === "count")
            .forEach((count) => {
              booking.counts.push(count.attributes);
            });
        }
      });
    }

    if (element.relationships.gifts && element.relationships.gifts.data) {
      element.relationships.gifts.data.forEach((item) => {
        if (responseData.bookings.included) {
          responseData.bookings.included
            .filter((inc) => inc.id == item.id && inc.type === "gift")
            .forEach((gift) => {
              booking.gifts.push(gift.attributes);
            });
        }
      });
    }

    // Парсинг дат в moment объекты
    booking._start = moment(booking.start_date, [
      "DD-MM-YYYY H:mm",
      "DD-MM-YYYY HH:mm",
    ]);
    booking._end = moment(booking.end_date, [
      "DD-MM-YYYY H:mm",
      "DD-MM-YYYY HH:mm",
    ]);

    return booking;
  });

  return { cars, bookings };
}
